import React, { Fragment, useEffect, useState } from 'react';
import Grid from '@material-ui/core/Grid';
import HoldsTable from './HoldsTable';
import Divider from '@material-ui/core/Divider';
import { LoanService } from '../../services';
import { makeStyles } from '@material-ui/core/styles';
import { useError } from '../../contexts/ErrorContext';
import { centsToDollar } from '../../utils/formatters';
import InlineLoadingText from '../../components/InlineLoadingText';
import { CountAmountItem } from './CountAmountLineItem';

const styles = makeStyles((theme) => ({
    table: {
        marginTop: '30px',
        width: '85vw',
        marginRight: '5vw'
    },
    summaryContainer: {
        paddingLeft: '50px',
        paddingRight: '50px',
        width: '85vw'
    },
    summaryHeader: {
        textAlign: 'center'
    },
    summaryTotals: {
        paddingTop: '10px',
        paddingBottom: '10px'
    },
    summaryBody: {
        paddingTop: '10px',
        paddingBottom: '10px'
    },
    summary: {
        backgroundColor: theme.palette.grey.main,
        borderRadius: '5px',
        border: 'groove',
        borderColor: 'black',
        borderWidth: '0.5px',
    }
}));

const Holds = ({ selectedDate, autoSend }) => {
    const [holds, setHolds] = useState(null);
    const [holdSummary, setHoldSummary] = useState(null);
    const [holdMetrics, setHoldMetrics] = useState(null);
    const { showError } = useError();
    const classes = styles();

    useEffect(() => {
        let isMounted = true;

        const fetchHolds = async () => {
            try {
                const holdsResponse = await LoanService.getHeldLoans();
                if (isMounted) {
                    if (holdsResponse.data?.heldLoans) {
                        setHolds(holdsResponse.data.heldLoans);
                    } else {
                        setHolds([]);
                    }
                }
            } catch (error) {
                if (isMounted) {
                    console.error('Error fetching held loans:', error);
                    setHolds([]);
                    showError(error, "Failed to retrieve loan holds");
                }
            }

            try {
                const summaryResponse = await LoanService.getHeldLoanSummary();
                if (isMounted) {
                    if (summaryResponse.data?.loansOnHoldSummary) {
                        setHoldSummary(summaryResponse.data.loansOnHoldSummary);
                        setHoldMetrics(summaryResponse.data.loansOnHoldSummary.paymentHoldMetrics);
                    } else {
                        setHoldSummary(null);
                        setHoldMetrics(null);
                    }
                }
            } catch (error) {
                if (isMounted) {
                    console.error('Error fetching held loan summary:', error);
                    setHoldSummary({});
                    setHoldMetrics(null);
                    showError(error, "Failed to retrieve loan hold summary");
                }
            }
        };

        fetchHolds();

        return () => {
            isMounted = false;
        };
    }, [showError]);

    return holds === null || holdSummary === null ? (
        <InlineLoadingText/>
    ) : (
        <Fragment>
            <Grid container direction="column">
                <Grid item className={classes.summaryContainer}>
                    <h3 className={classes.summaryHeader}>Summary</h3>
                    <Grid container direction="column" className={classes.summary}>
                        <Grid item>
                            <Grid container justifyContent="space-evenly" className={classes.summaryBody}>
                                <Grid item className={classes.summaryTotals}>
                                    <li><b>Total # of holds: </b> {holds.length || 0} </li>
                                    <li><b>Total # of payment holds: </b>{holdSummary?.totals?.totalPaymentCount || 0} </li>
                                    <li><b>Total # of funding request holds: </b> {holdSummary?.totals?.totalFundingCount || 0}</li>
                                    <li><b>Total # of queued pending releases:</b> {holdMetrics?.pending || 0} </li>
                                </Grid>
                                <Grid item className={classes.summaryTotals}>
                                    <li><b>Total amount held: </b>{centsToDollar((holdSummary?.totals?.totalPaymentAmountCents || 0) + (holdSummary?.totals?.totalFundingAmountCents || 0))} </li>
                                    <li><b>Total payment amount held: </b>{centsToDollar(holdSummary?.totals?.totalPaymentAmountCents || 0)}</li>
                                    <li><b>Total funding request amount held: </b> {centsToDollar(holdSummary?.totals?.totalFundingAmountCents || 0)} </li>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Divider/>
                        <Grid item>
                            <Grid container spacing={2} justifyContent="space-evenly" direction="row" className={classes.summaryBody} >
                                {(holdSummary?.lenderSummary || []).map((row, index) => (
                                <Grid item key={`${row.lenderName}-${index}`}>
                                    <p><b><u>{row.lenderName}</u></b></p>
                                    <li>Total Payment: {centsToDollar(row.paymentAmountCents || 0)}</li>
                                    <li>Payment Count: {row.paymentCount || 0}</li>
                                    <li>Total Requested Funding: {centsToDollar(row.fundingAmountCents || 0)}</li>
                                    <li>Funding Request Count: {row.fundingCount || 0}</li>
                                </Grid>
                                ))}
                            </Grid>
                        </Grid>
                        <Divider/>
                        <Grid item>
                            <Grid container spacing={2} justifyContent="space-evenly" direction="row" className={classes.summaryTotals}>
                                <Grid>
                                    <CountAmountItem label="NSF count / amount" count={holdMetrics?.nsfCount} amount={holdMetrics?.nsfAmount} />
                                    <CountAmountItem label="S1 count / amount" count={holdMetrics?.s1Count} amount={holdMetrics?.s1Amount} />
                                </Grid>
                                <Grid>
                                    <CountAmountItem label="S2 count / amount" count={holdMetrics?.s2Count} amount={holdMetrics?.s2Amount} />
                                    <CountAmountItem label="S3 count / amount" count={holdMetrics?.s3Count} amount={holdMetrics?.s3Amount} />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item className={classes.table}>
                    <HoldsTable holds={holds} selectedDate={selectedDate}  autoSend={autoSend} />
                </Grid>
            </Grid>
        </Fragment>
    )
}

export default Holds;
